import { isNaN, isNil, isNumber, isString, omit } from 'lodash-es';
import dayjs from 'dayjs';
import { type AxiosError } from 'axios';
import {
  ONE_DAY,
  ONE_DAY_IN_SECOND,
  ONE_HOUR,
  ONE_MINUTE,
  ONE_SECOND,
  type DBType,
  type DetailInfoData,
  type UserTags,
} from '@/common/utils';
import { DB_TYPE } from '@/common/define/common.define';
import { DISPLAY_DB_TYPE, MINIMAL_INTERVAL } from '@/database/utils/define';
import { isExceededCurrentTime } from '@/common/components/molecules/timeController/utils';
import {
  convertMsToSec,
  generateUUID,
  getDataArrangeIntervalByTimeRange,
  standardTimeToUtcZeroTime,
  utcZeroTimeToStandardTime,
} from '@/common/utils/commonUtils';
import { UTC_TIME_FORMAT } from '@/common/components/molecules/timeController/constants';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import type { TiberoAdditionalDataItem } from '@/api-client/tibero/model';
import type { WaitClassMetricItemV7, WaitClassMetricsV7 } from '@/api-client/oracle/model';
import { ONE_MINUTE_BY_SECONDS } from '@/login/components/loginForm/loginForm.define';
import { isNilOrEmpty } from '@/database/utils/validation.utils';
import { INSTANCE_ROLE } from '@/config/views/platform/database/instance/configDatabase.define';
import { MetricResponse } from '@/api-client/data/model';
import type { ClusterData, ClusterDataWithChildren, ReplicationRole } from './types';
import type { InstanceInfo, InstanceGroupInfo } from './pa/pa.types';

export const getPortAsNumber = (port: string | number | null | undefined): number | undefined => {
  return isNil(port) || port === '' || isNaN(+port) ? undefined : +port;
};

export const isUnavailableIdInSession = (sessionId?: number) =>
  sessionId === -1 || sessionId == null;

export class MetricFailError extends Error {
  constructor({ reason }) {
    super(reason);
    this.name = 'MetricFailError';
  }

  getErrorStatusText() {
    if (this.message === 'null') {
      return 'Unknown Error';
    }
    return this.message ?? 'Unknown Error';
  }
}

/**
 * null을 제외한 배열의 최댓값 인덱스를 반환한다.
 * 동일한 최댓값이 여러 개면 마지막 인덱스를 반환한다.
 */
export const getMaxValLastIdx = (arr: (number | null)[]): number => {
  let maxVal = -Infinity;
  let maxIdx = 0;

  for (let i = 0; i < arr.length; i++) {
    const val = arr[i];
    if (val !== null && val >= maxVal) {
      maxVal = val;
      maxIdx = i;
    }
  }

  return maxIdx;
};

export const checkValidMetric = (data: MetricResponse[]) => {
  data.forEach(({ status, reason }) => {
    if (status === 'fail') {
      throw new MetricFailError({ reason });
    }
  });
};

export const mapMetricNamesToDataIds = ({
  dbType,
  metricNames = [],
}: {
  dbType: DBType;
  metricNames: string[];
}) => {
  const mappedMetricNames: string[] = [];
  metricNames.forEach((name) => {
    if (!name) return;
    mappedMetricNames.push(name.startsWith(`db_${dbType}_`) ? name : `db_${dbType}_${name}`);
  });
  return mappedMetricNames;
};

export const mapDbTypeToDisplay = (dbType?: string) =>
  dbType ? DISPLAY_DB_TYPE[dbType.toUpperCase()] : '';

interface ClusterTreeGridMapperOption {
  setClusterUncheckable?: boolean;
  setClusterExpanded?: boolean;
  setInitialParent?: boolean;
  initialValues?: any;
  setCustomGroupingUncheckable?: boolean; // cluster, grouping
}

export const getIsCluster = <T extends ClusterData>(item: T) => {
  return item.isCluster || item.instanceRole === INSTANCE_ROLE.SINGLE_STORE_CLUSTER_INSTANCE;
};

const getIsParentTarget = <T extends ClusterData>(item: T) => {
  return (
    item.platform === 'aws' ||
    item.platform === 'azure' ||
    item.instanceRole === INSTANCE_ROLE.SINGLE_STORE_CLUSTER_INSTANCE
  );
};

const sortByIsCluster = <T extends ClusterData>(instances: T[]) =>
  instances.sort((a, b) => {
    const aIsCluster = getIsCluster(a) ? 1 : 0;
    const bIsCluster = getIsCluster(b) ? 1 : 0;
    return bIsCluster - aIsCluster;
  });

export const clusterTreeGridMapper = <T extends ClusterData>(
  data?: T[], // rowData 의 child node들
  parentData?: T,
  nameField: keyof T = 'name' as keyof T,
  option?: ClusterTreeGridMapperOption,
): (ClusterDataWithChildren<T> | T)[] => {
  if (!data) {
    return [];
  }
  const clusterNameMapper: Record<string, number> = {}; // clusterId <-> 배열 index mapper
  const dataToReturn: (ClusterDataWithChildren<T> | T)[] = [];

  sortByIsCluster(data).forEach((item) => {
    // item: 인스턴스 또는 클러스터 또는 클러스터링 (aws)
    const {
      clusterId: resClusterId,
      clusterName: resClusterName,
      singlestoreClusterId,
    } = item || {};

    const isCluster = getIsCluster<T>(item);
    const clusterId = resClusterId || singlestoreClusterId;
    const clusterName = resClusterName || (singlestoreClusterId && item[nameField]);

    if (!clusterId) {
      // 어떠한 클러스터에도 속해있지 않은 일반 인스턴스일 경우
      dataToReturn.push(item);
      return;
    }

    // 클러스터 또는 클러스터링 하위 인스턴스 체크
    const parentIndex = clusterNameMapper[clusterId];
    if (!isNumber(parentIndex)) {
      // clusterNameMapper 에 클러스터 아이디 등록되어 있지 않을 경우
      if (getIsParentTarget<T>(item) && isCluster) {
        // 클러스터 표현 > 부모 인스턴스가 실제 타겟으로 사용
        clusterNameMapper[clusterId] = dataToReturn.length; // new index
        dataToReturn.push({
          ...item,
          isCluster: true,
          type: item.type,
          instanceGroupName: item.instanceGroupName,
          expand: option?.setClusterExpanded ?? true,
          uncheckable: option?.setClusterUncheckable,
          children: [],
        });
        return;
      }
      if (clusterId) {
        // 클러스터링 (일반 그룹화)
        clusterNameMapper[clusterId] = dataToReturn.length; // new index
        if (option?.setInitialParent) {
          // 부모의 값을 초기화 하는 경우 초기화 변수(initialValues) 설정
          dataToReturn.push({
            ...option?.initialValues,
            clusterId,
            [nameField]: clusterName,
            isClustering: true,
            type: item.type,
            instanceGroupName: item.instanceGroupName,
            expand: option?.setClusterExpanded ?? true,
            uncheckable: option?.setCustomGroupingUncheckable ?? false,
            children: [{ ...item, uncheckable: false }] as ClusterData[],
          });
        } else {
          dataToReturn.push({
            ...(parentData ?? item),
            clusterId,
            [nameField]: clusterName,
            isClustering: true,
            type: item.type,
            instanceGroupName: item.instanceGroupName,
            expand: option?.setClusterExpanded ?? true,
            uncheckable: option?.setCustomGroupingUncheckable ?? false,
            children: [{ ...item, uncheckable: false }] as ClusterData[],
          });
        }

        return;
      }
    }

    if (
      !isNumber(parentIndex) ||
      !(dataToReturn[parentIndex] as ClusterDataWithChildren<T>)?.children
    ) {
      // 클러스터 또는 클러스터링의 하위 인스턴스가 아닐 경우
      dataToReturn.push(item);
      return;
    }

    // 클러스터 또는 클러스터링 하위 인스턴스일 경우 (parentIndex 찾음)
    (dataToReturn[parentIndex] as ClusterDataWithChildren<T>)?.children?.push(item);
  });

  const awsGrouping: Record<string, any> = {};
  dataToReturn.forEach((item) => {
    const { groupId: awsGroupingId, groupName: awsGroupingName } = item as any;
    if (awsGroupingId) {
      if (!awsGrouping[awsGroupingId]) {
        const initialObj = option?.setInitialParent ? option?.initialValues : {};
        awsGrouping[awsGroupingId] = {
          ...initialObj,
          [nameField]: awsGroupingName,
          awsGroupingId,
          type: item.type,
          instanceGroupName: item.instanceGroupName,
          uncheckable: option?.setCustomGroupingUncheckable ?? false,
          children: [],
        };
      }
      awsGrouping[awsGroupingId].children.push({
        ...item,
        uncheckable: false,
      });
      return;
    }

    awsGrouping[generateUUID()] = item;
  });

  return Object.values(awsGrouping);
};

export const isDBTypeValid = (value: any): value is NonNullable<DBType> => {
  return typeof value === 'string' && (Object.values(DB_TYPE) as string[]).includes(value);
};

export const getIntervalByDbTypeAndTimePeriod = (
  dbType: DBType,
  { fromTimeUtc, toTimeUtc }: Partial<TimePeriodInfo>,
) => {
  if (!fromTimeUtc || !toTimeUtc) {
    return MINIMAL_INTERVAL;
  }
  const fromTimeUtcDayjs = isString(fromTimeUtc) ? dayjs(fromTimeUtc) : fromTimeUtc;
  const toTimeUtcDayjs = isString(toTimeUtc) ? dayjs(toTimeUtc) : toTimeUtc;
  const newInterval = getDataArrangeIntervalByTimeRange(toTimeUtcDayjs.diff(fromTimeUtcDayjs));
  if (
    dbType === DB_TYPE.MYSQL ||
    dbType === DB_TYPE.POSTGRESQL ||
    dbType === DB_TYPE.SINGLESTORE ||
    dbType === DB_TYPE.CLICKHOUSE
  ) {
    return newInterval === 'I1m' || newInterval === 'I5s' ? 'I10m' : newInterval;
  }
  return newInterval === 'I5s' ? MINIMAL_INTERVAL : newInterval;
};

export const getIntervalByDbTypeAndTimePeriodTopN = (
  dbType: DBType,
  { fromTimeUtc, toTimeUtc }: Partial<TimePeriodInfo>,
) => {
  if (!fromTimeUtc || !toTimeUtc) {
    return MINIMAL_INTERVAL;
  }
  const fromTimeUtcDayjs = isString(fromTimeUtc) ? dayjs(fromTimeUtc) : fromTimeUtc;
  const toTimeUtcDayjs = isString(toTimeUtc) ? dayjs(toTimeUtc) : toTimeUtc;
  const timeRange = toTimeUtcDayjs.diff(fromTimeUtcDayjs);
  // NOTE: 1일 이상일 경우, 최소 조회시간 1시간으로 고정, 그외 1분
  if (timeRange <= ONE_DAY) {
    return MINIMAL_INTERVAL;
  }
  return 'I1h';
};

export const getIntervalByDbTypeAndTimePeriodTrend = ({
  fromTime,
  toTime,
}: Partial<TimePeriodInfo>) => {
  if (!fromTime || !toTime) {
    return MINIMAL_INTERVAL;
  }
  const fromTimeDayjs = isString(fromTime) ? dayjs(fromTime) : fromTime;
  const toTimeDayjs = isString(toTime) ? dayjs(toTime) : toTime;
  const diffInMinutes = toTimeDayjs.diff(fromTimeDayjs, 'm');

  const DAY = ONE_DAY_IN_SECOND / ONE_MINUTE_BY_SECONDS;
  if (diffInMinutes <= 2 * DAY) {
    return MINIMAL_INTERVAL;
  }
  if (diffInMinutes <= 7 * DAY) {
    return 'I10m';
  }
  return 'I1h';
};

export const getTargetTagInfoData = (targetTagList?: UserTags[]): DetailInfoData[] => {
  if (!targetTagList?.length) {
    return [];
  }

  return targetTagList.map((tag) => {
    return {
      label: tag.tagName ?? '',
      field: tag.tagName ?? '',
      values: tag.tagValues ?? [],
      renderType: 'label',
    };
  });
};

export const getTimeRangeBelowCur = ({
  fromTimeUtc,
  toTimeUtc,
}: {
  fromTimeUtc: string;
  toTimeUtc: string;
}) => {
  const fromTime = utcZeroTimeToStandardTime(fromTimeUtc);
  const toTime = utcZeroTimeToStandardTime(toTimeUtc);
  const currentTime = dayjs.tz().format(UTC_TIME_FORMAT);

  const isExceeded = isExceededCurrentTime({
    time: toTime,
  });

  return {
    fromTime,
    toTime: isExceeded ? currentTime : toTime,
    fromTimeUtc,
    toTimeUtc: isExceeded ? standardTimeToUtcZeroTime(currentTime) : toTimeUtc,
  };
};

// type은 makeRows에 맞춘 것
export const convertMsToSecNumber = (
  value: string | number | undefined,
): string | number | undefined => {
  if (isNil(value)) {
    return undefined;
  }

  const convertedValue: string = convertMsToSec(value);
  const convertedValueNumber = parseFloat(convertedValue.replace(/,/g, ''));
  if (Number.isNaN(convertedValueNumber)) {
    return value;
  }

  return convertedValueNumber;
};

export const mysqlLatencyColumnFormatter = {
  statementLatency: (value?: number | string | null) => (isNil(value) ? null : value.toString()),
  lastStatementLatency: (value?: number | string | null) =>
    isNil(value) ? null : value.toString(),
  lastWaitLatency: (value?: number | string | null) => (isNil(value) ? null : value.toString()),
};

export const getAxiosErrorFromApiError = <T>(
  error: AxiosError<T> | null | { ignoreRequest: boolean },
): AxiosError<T> | null => {
  if (error && 'ignoreRequest' in error) {
    return null;
  }
  return error;
};

export const getMatchingAdditionalDataTibero = (
  response: TiberoAdditionalDataItem[],
  childNumber?: number | null,
) => {
  if (isNilOrEmpty(childNumber)) {
    return response[0] || {};
  }
  return response.find((item) => item.childNumber === childNumber) || {};
};

export const getNodeInstanceIdForSinglestore = (clusterId?: string, nodeId?: number) => {
  if (!clusterId || isNil(nodeId)) {
    return '';
  }
  return `${clusterId}|${nodeId}`;
};

export const parseInstanceId = (instanceId: string) => {
  return instanceId.split('|')[0];
};

export const nodeIdFilterCallback = ({ instanceId }: InstanceInfo) =>
  instanceId && !instanceId.includes('|');

export const checkUseCommonCategory = (dbType: DBType): boolean => {
  return !(dbType === DB_TYPE.REDSHIFT || dbType === DB_TYPE.SINGLESTORE);
};

export const getReplicationRole = (replicationRole: string): ReplicationRole => {
  switch (replicationRole) {
    case 'Standby':
      return 'Standby';
    case 'Primary':
      return 'Primary';
    default:
      throw new Error(`Invalid replication role: ${replicationRole}`);
  }
};

export const compareAnyType = (
  a?: string | number | boolean | null,
  b?: string | number | boolean | null,
): number => {
  if (typeof b !== 'number' && typeof a !== 'number') {
    return 0;
  }
  if (typeof a !== 'number') {
    return 1;
  }
  if (typeof b !== 'number') {
    return -1;
  }
  return b - a;
};

// TODO: remove
export const getGridColumnFieldSuffixByToggle = (type: string) => (type === 'sum' ? '' : 'Avg');
// TODO: remove
export const getRedshiftGridColumnFieldSuffixByToggle = (type: string) =>
  type === 'sum' ? 'Sum' : 'Avg';

export const getRtmSelectedInfo = ({
  idInfo: { folderId, instanceId },
  folderList,
  dbType,
  hasInstanceId = true, // singleView: true, multiView: false
  viewType = '',
  clusterDiffKey = '',
}: {
  idInfo: { folderId?: string; instanceId?: string };
  folderList: InstanceGroupInfo[];
  dbType: DBType;
  hasInstanceId?: boolean;
  viewType?: string;
  clusterDiffKey?: string;
}): {
  folderId?: string;
  instanceId?: string;
  selectedInfo?: InstanceGroupInfo | InstanceInfo;
} => {
  const selectedInfo = {
    folderId: '',
    instanceId: '',
    selectedInfo: {} as InstanceGroupInfo | InstanceInfo,
  };

  if (!folderList?.length) {
    return selectedInfo;
  }

  const filteredFolderList = folderList.filter(
    (item) =>
      !!item.instances?.length &&
      item.instances.some((instance) => instance.type === dbType && instance.enabled),
  );
  if (!filteredFolderList.length) {
    return selectedInfo;
  }

  if (viewType?.toLowerCase() === 'clusterview') {
    const folderInfo = filteredFolderList.find(
      (v) =>
        (v.instances || []).some((item) => item?.[clusterDiffKey] === instanceId) ||
        v.folderId === folderId,
    );
    const selectedClusterInfo = (folderInfo?.instances || []).find(
      (item) => item.instanceId === instanceId,
    );
    const clusterInfoList = folderInfo?.instances?.filter(
      (item) =>
        item.type === dbType &&
        item.enabled &&
        item?.[clusterDiffKey] === instanceId &&
        item.instanceId !== item?.[clusterDiffKey],
    );

    return {
      folderId: folderInfo?.folderId,
      instanceId,
      selectedInfo: { instances: clusterInfoList ?? [], name: selectedClusterInfo?.name },
    };
  }

  // Folder ID 검증: folderId 없거나 유효하지 않을 경우, 첫번째 폴더의 첫번째 인스턴스 선택
  const isValidFolderId = filteredFolderList.some((v) => v.folderId === folderId);
  if (!folderId || !isValidFolderId) {
    const firstFolderInfo = filteredFolderList[0];
    const firstInstanceInfo = (firstFolderInfo?.instances || []).filter(
      (item) => item.type === dbType && item.enabled,
    )[0];
    return {
      folderId: firstFolderInfo?.folderId,
      instanceId: firstInstanceInfo?.instanceId,
      selectedInfo: hasInstanceId ? firstInstanceInfo : firstFolderInfo,
    };
  }

  // 1. multiView일 경우
  if (!hasInstanceId) {
    const folderInfo = filteredFolderList.find((v) => v.folderId === folderId);

    return {
      folderId: folderInfo?.folderId,
      instanceId: '',
      selectedInfo: folderInfo,
    };
  }

  // Instance ID 검증: instanceId 없거나 유효하지 않을 경우, 선택된 폴더의 첫번째 인스턴스 선택
  // 2. singleView일 경우
  const folderInfo = filteredFolderList.find((v) => v.folderId === folderId);
  const isValidInstanceId = folderInfo?.instances?.some(
    (v) => v.instanceId === instanceId && v.enabled,
  );
  if (!instanceId || !isValidInstanceId) {
    const firstInstanceInfo = folderInfo?.instances?.filter(
      (item) => item.type === dbType && item.enabled,
    )[0];
    return {
      folderId: folderInfo?.folderId,
      instanceId: firstInstanceInfo?.instanceId,
      selectedInfo: firstInstanceInfo,
    };
  }

  // folderId, instanceId 모두 유효할 경우, 선택 정보 반환
  const instanceInfo = folderInfo?.instances?.find((v) => v.instanceId === instanceId && v.enabled);
  return {
    folderId: folderInfo?.folderId,
    instanceId: instanceInfo?.instanceId,
    selectedInfo: instanceInfo,
  };
};

const REDSHIFT_GRID_FORMATTERS = {
  suspended: (val?: number) => {
    if (isNil(val)) {
      return val;
    }
    return val === 1 ? 'T' : 'F';
  },
  insertPristine: (val?: number) => {
    if (isNil(val)) {
      return val;
    }
    return val === 1 ? 'T' : 'F';
  },
  recordTime: (val) => {
    if (isNil(val)) {
      return val;
    }
    return `${dayjs(val).format(UTC_TIME_FORMAT)}+00`;
  },
  passwordExpiration: (val) => {
    if (isNil(val)) {
      return val;
    }
    return `${dayjs(val).format(UTC_TIME_FORMAT)}+00`;
  },
} as const;

export const getRedshiftFormatter = (fields?: string[]) => {
  if (!fields) {
    return REDSHIFT_GRID_FORMATTERS;
  }

  return fields.reduce((acc, field) => {
    acc[field] = REDSHIFT_GRID_FORMATTERS[field];
    return acc;
  }, {});
};

const formatTFValue = (value?: number) => {
  if (isNil(value)) {
    return value;
  }
  return value === 1 ? 'T' : 'F';
};
const formatRedshiftTimeValue = (value?: string) => {
  if (isNil(value)) {
    return value;
  }
  return `${dayjs(value).format(UTC_TIME_FORMAT)}+00`;
};
export const formatRedshiftRow = (rawData: any) => {
  const data = {
    ...omit(rawData, ['queryInfo', 'userInfo']),
    ...(rawData?.queryInfo ?? {}),
    ...(rawData?.userInfo ?? {}),
  };
  return {
    ...data,
    sqlId: data.normalizedSqlId ?? '',
    suspended: formatTFValue(data.suspended),
    insertPristine: formatTFValue(data.insertPristine),
    recordTime: formatRedshiftTimeValue(data.recordTime),
    passwordExpiration: formatRedshiftTimeValue(data.passwordExpiration),
  };
};

export const makeOracleWaitClassRows = (data: WaitClassMetricItemV7[]) => {
  return (data || []).reduce<WaitClassMetricsV7 & { waitClassName?: string }[]>((acc, item) => {
    if (item?.values?.length) {
      item.values.forEach((val) => {
        acc.push({
          waitClassName: item.waitClassName,
          ...val,
        });
      });
    }
    return acc;
  }, []);
};

// @deprecated use getTimePeriodByRange instead
export const getTimePeriod = (fromTime: string, toTime: string) => {
  const diff = dayjs(toTime).diff(dayjs(fromTime));
  const days = Math.floor(diff / ONE_DAY);
  if (days > 0) {
    return `p${days}d`;
  }
  const hours = Math.floor(diff / ONE_HOUR);
  if (hours > 0) {
    return `p${hours}h`;
  }
  const minutes = Math.floor(diff / ONE_MINUTE);
  if (minutes > 0) {
    return `p${minutes}m`;
  }
  const seconds = Math.floor(diff / ONE_SECOND);
  return `p${seconds}s`;
};
